import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublicArtists } from '../../hooks/usePublicArtists';
import { formatPublicArtistRole, PublicArtist } from '../../types/publicArtist';
import './ArtistCommunitySection.css';

/** Bento tile layout — index maps to grid slot; `size` controls column/row span. */
const TILE_LAYOUTS: { size: 'normal' | 'wide' | 'tall'; mobileHidden?: boolean }[] = [
  { size: 'wide' },
  { size: 'normal' },
  { size: 'tall' },
  { size: 'normal' },
  { size: 'normal', mobileHidden: true },
  { size: 'wide', mobileHidden: true },
  { size: 'normal' },
  { size: 'normal' },
  { size: 'normal', mobileHidden: true },
  { size: 'tall', mobileHidden: true },
  { size: 'normal', mobileHidden: true },
];

const ROTATE_MS = 3200;
const TRANSITION_MS = 450;

interface TileState {
  artist: PublicArtist | null;
  phase: 'idle' | 'leaving' | 'entering';
}

function roleClass(role: PublicArtist['role'] | null): string {
  if (role === 'producer') return 'artist-wall__role--producer';
  if (role === 'rapper') return 'artist-wall__role--rapper';
  if (role === 'producer_rapper') return 'artist-wall__role--producer-rapper';
  return '';
}

const ArtistCommunitySection: React.FC = () => {
  const { loading, error, ready, hasArtists, totalCount, takeNextArtist } = usePublicArtists();
  const takeRef = useRef(takeNextArtist);
  takeRef.current = takeNextArtist;

  const tileCount = TILE_LAYOUTS.length;
  const [tiles, setTiles] = useState<TileState[]>(() =>
    Array.from({ length: tileCount }, () => ({ artist: null, phase: 'idle' as const }))
  );
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;

  const fillTile = useCallback((index: number) => {
    const next = takeRef.current();
    if (!next) return;
    setTiles(prev => {
      const copy = [...prev];
      copy[index] = { artist: next, phase: 'entering' };
      return copy;
    });
    window.setTimeout(() => {
      setTiles(prev => {
        const copy = [...prev];
        if (copy[index]?.phase === 'entering') {
          copy[index] = { ...copy[index], phase: 'idle' };
        }
        return copy;
      });
    }, TRANSITION_MS);
  }, []);

  // Seed every tile once data is ready.
  useEffect(() => {
    if (!ready || !hasArtists) return;
    setTiles(prev =>
      prev.map((tile, i) => {
        if (tile.artist) return tile;
        const artist = takeRef.current();
        return artist ? { artist, phase: 'idle' as const } : tile;
      })
    );
  }, [ready, hasArtists]);

  // Staggered rotation: one random tile at a time.
  useEffect(() => {
    if (!ready || !hasArtists) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return undefined;

    const interval = window.setInterval(() => {
      const current = tilesRef.current;
      const rotatable = current
        .map((t, i) => ({ t, i }))
        .filter(({ t }) => t.artist && t.phase === 'idle');
      if (rotatable.length === 0) return;

      const pick = rotatable[Math.floor(Math.random() * rotatable.length)];
      const idx = pick.i;

      setTiles(prev => {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], phase: 'leaving' };
        return copy;
      });

      window.setTimeout(() => {
        fillTile(idx);
      }, TRANSITION_MS);
    }, ROTATE_MS);

    return () => window.clearInterval(interval);
  }, [ready, hasArtists, fillTile]);

  const artistLabel = useMemo(() => {
    if (totalCount === null) return '';
    if (totalCount === 1) return '1 artist on the platform';
    return `${totalCount} artists on the platform`;
  }, [totalCount]);

  if (loading) {
    return (
      <section className="artist-wall" aria-labelledby="artist-wall-heading">
        <div className="artist-wall__inner max-w-7xl mx-auto container-padding">
          <div className="artist-wall__header">
            <span className="artist-wall__eyebrow">Community</span>
            <h2 id="artist-wall-heading" className="artist-wall__title">
              Artists already here
            </h2>
          </div>
          <div className="artist-wall__grid artist-wall__grid--loading" aria-hidden>
            {TILE_LAYOUTS.map((layout, i) => (
              <div
                key={i}
                className={`artist-wall__tile artist-wall__tile--${layout.size} ${
                  layout.mobileHidden ? 'artist-wall__tile--mobile-hidden' : ''
                } artist-wall__tile--skeleton`}
              />
            ))}
            <div className="artist-wall__tile artist-wall__tile--join artist-wall__tile--skeleton" />
          </div>
        </div>
      </section>
    );
  }

  if (error || !hasArtists) {
    return null;
  }

  return (
    <section className="artist-wall" aria-labelledby="artist-wall-heading">
      <div className="artist-wall__inner max-w-7xl mx-auto container-padding">
        <div className="artist-wall__header">
          <span className="artist-wall__eyebrow">Community</span>
          <h2 id="artist-wall-heading" className="artist-wall__title">
            Artists already here
          </h2>
          <p className="artist-wall__lead">
            Producers and rappers from across India — {artistLabel} and growing.
          </p>
        </div>

        <div className="artist-wall__grid" aria-live="polite">
          {TILE_LAYOUTS.map((layout, i) => {
            const tile = tiles[i];
            const artist = tile?.artist;
            return (
              <div
                key={i}
                className={[
                  'artist-wall__tile',
                  `artist-wall__tile--${layout.size}`,
                  layout.mobileHidden ? 'artist-wall__tile--mobile-hidden' : '',
                  tile?.phase === 'leaving' ? 'artist-wall__tile--leaving' : '',
                  tile?.phase === 'entering' ? 'artist-wall__tile--entering' : '',
                  artist ? roleClass(artist.role) : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {artist && (
                  <div className="artist-wall__tile-content">
                    <span className="artist-wall__username">{artist.username}</span>
                    <span className="artist-wall__role">{formatPublicArtistRole(artist.role)}</span>
                    {artist.state && (
                      <span className="artist-wall__state">{artist.state}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <Link to="/signup" className="artist-wall__tile artist-wall__tile--join">
            <span className="artist-wall__join-icon" aria-hidden>
              +
            </span>
            <span className="artist-wall__join-text">Join them</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ArtistCommunitySection;
